import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import {
  Snackbar,
  Alert,
  Paper,
} from "@mui/material";
import ListLayout from "../../../components/Layout/ListLayout";
import ThemeConfigurationForm from "../../../components/Admin/ThemeConfiguration/ThemeConfigurationForm";
import {
  getGetThemeConfigurationQueryOptions,
  getSaveThemeConfigurationMutationOptions,
} from "../../../api/bpm/endpoints";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useAuth } from "../../../auth";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/__auth/admin/theme")({
  component: ThemeConfigurationComponent,
  staticData: {
    title: "routes.appearance",
  },
});

function ThemeConfigurationComponent() {
    const { t } = useTranslation();
  const { currentTenant } = useAuth();
  const effectiveTenant = currentTenant || "default-domain";
  const { data: themeData, isLoading: isLoadingTheme, refetch } = useQuery({
    ...getGetThemeConfigurationQueryOptions({ tenant: effectiveTenant }),
    enabled: !!effectiveTenant
  });
  const saveMutation = useMutation(getSaveThemeConfigurationMutationOptions());

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const showSnackbar = (message: string, severity: "success" | "error" = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const initialData = useMemo(() => {
    if (!themeData) return null;
    try {
      return JSON.parse(themeData as unknown as string);
    } catch (e) {
      console.error("Erro ao parsear tema", e);
      return null;
    }
  }, [themeData]);

  const handleSubmit = async (updatedTheme: any) => {
    try {
      await saveMutation.mutateAsync({
        params: {
          tenant: effectiveTenant,
          theme: JSON.stringify(updatedTheme)
        }
      });
      showSnackbar(t("admin.theme.savedSuccess"));
      refetch();
    } catch (err: any) {
      const errorMessage = err.body?.message || t("admin.theme.errorSaving");
      showSnackbar(errorMessage, "error");
    }
  };

  return (
    <ListLayout
      title={t("admin.theme.title")}
      subtitle={t("admin.theme.subtitle")}
      isLoading={isLoadingTheme}
    >
      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, boxShadow: (theme) => theme.shadows[2] }}>
        <ThemeConfigurationForm
          initialData={initialData}
          onSubmit={handleSubmit}
          isLoading={saveMutation.isPending}
        />
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </ListLayout>
  );
}
